import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Workflow } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Indicador, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, mesAno, numero, relativo } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { BASE, COR_ESTADO, type Cartao as CartaoAutomacao, type Painel as PainelDTO } from './tipos';

export const chavePainel = ['automacoes', 'painel'] as const;

/**
 * Painel das automações (fase 1 da proposta): o radar do mês no topo (no lugar da mensagem do dia 17) e um cartão por
 * automação com o estado, a última e a próxima execução, o placar do mês e a fila humana. Atualiza sozinho a cada minuto.
 */
export function Painel() {
  const { t } = useTranslation('automacoes');
  const s = useSessao();
  const q = useQuery({ queryKey: chavePainel, queryFn: () => get<PainelDTO>('/automacoes/automacoes'), refetchInterval: 60_000 });
  // Val5 (29/10): aplicar o catálogo é configuração (`automacoes.configurar`).
  const podeEditar = s.pode('automacoes.configurar');
  return (
    <Estado consulta={q}>
      {(p) =>
        p.itens.length === 0 ? (
          <Cartao>
            <Vazio
              icone={<Workflow className="size-5" aria-hidden />}
              titulo={t('vazio.titulo')}
              texto={podeEditar ? t('vazio.texto') : t('vazio.textoLeitura')}
              acao={podeEditar ? <BotaoCatalogo /> : undefined}
            />
          </Cartao>
        ) : (
          <div className="flex min-w-0 flex-col gap-6" data-painel-automacoes>
            <Radar p={p} />
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {p.itens.map((a) => (
                <li key={a.id} className="min-w-0">
                  <CartaoDaAutomacao a={a} />
                </li>
              ))}
            </ul>
          </div>
        )
      }
    </Estado>
  );
}

function BotaoCatalogo() {
  const { t } = useTranslation('automacoes');
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => post<{ criadas: number }>('/automacoes/automacoes/catalogo', {}),
    onSuccess: async (r) => {
      toast.sucesso(t('vazio.cadastradas', { count: r.criadas }));
      await qc.invalidateQueries({ queryKey: ['automacoes'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <AcaoPrimaria>
      <Botao variante="primario" carregando={m.isPending} onClick={() => m.mutate()}>
        {t('vazio.cadastrar')}
      </Botao>
    </AcaoPrimaria>
  );
}

function Radar({ p }: { p: PainelDTO }) {
  const { t } = useTranslation('automacoes');
  const r = p.radar;
  return (
    <section aria-labelledby="radar-automacoes" className="flex min-w-0 flex-col gap-3">
      <h2 id="radar-automacoes" className="text-sm font-semibold text-tinta-2">
        {t('radar.titulo', { mes: mesAno(p.mes) })}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador rotulo={t('radar.execucoes')} valor={numero(r.execucoes)} detalhe={t('radar.execucoesDetalhe', { falhas: numero(r.falhas) })} cor={r.falhas ? 'perigo' : 'tinta-1'} />
        <Indicador rotulo={t('radar.processados')} valor={numero(r.processados)} detalhe={t('radar.processadosDetalhe', { ok: numero(r.ok) })} cor="verde" />
        <Indicador rotulo={t('radar.divergentes')} valor={numero(r.divergentes)} detalhe={t('radar.divergentesDetalhe', { aguardando: numero(r.aguardando) })} cor={r.divergentes ? 'laranja' : 'tinta-1'} />
        <Indicador rotulo={t('radar.fila')} valor={numero(r.pendencias_abertas)} detalhe={t('radar.filaDetalhe', { resolvidas: numero(r.resolvidas_no_mes) })} cor="ciano" />
      </div>
    </section>
  );
}

function CartaoDaAutomacao({ a }: { a: CartaoAutomacao }) {
  const { t } = useTranslation('automacoes');
  return (
    <article className="flex h-full min-w-0 flex-col gap-3 rounded-xl border border-borda bg-sup-1 p-4" data-automacao={a.codigo}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`${BASE}/automacao/${a.id}`} aria-label={t('cartao.abrir', { nome: a.nome })} className="block min-w-0 hover:text-acento-forte">
            <TextoCortado como="h3" className="text-base font-semibold">
              {a.nome}
            </TextoCortado>
          </Link>
          <p className="text-xs text-tinta-3">
            {t(`areas.${a.area}`)} · {t(`modos.${a.modo}`)}
          </p>
        </div>
        <div className="min-w-min shrink-0">
          <Selo cor={COR_ESTADO[a.estado]}>{t(`estados.${a.estado}`)}</Selo>
        </div>
      </div>
      <dl className="flex flex-col gap-1 text-sm">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-xs text-tinta-3">{t('cartao.ultima')}</dt>
          <dd className="min-w-0 text-right">
            <TextoCortado>{a.ultima ? `${relativo(a.ultima.iniciada_em)} · ${t(`execucao.${a.ultima.estado}`)}` : t('cartao.nunca')}</TextoCortado>
          </dd>
        </div>
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-xs text-tinta-3">{t('cartao.proxima')}</dt>
          <dd className="min-w-0 text-right">
            <TextoCortado>{a.proxima_em ? dataHora(a.proxima_em) : t('cartao.semAgenda')}</TextoCortado>
          </dd>
        </div>
      </dl>
      <p className="text-xs text-tinta-2">
        {t('cartao.placarMes', { processados: numero(a.mes.processados), ok: numero(a.mes.ok), divergentes: numero(a.mes.divergentes), aguardando: numero(a.mes.aguardando) })}
      </p>
      <div className="mt-auto">
        {a.fila ? <Selo cor="laranja">{t('cartao.fila', { count: a.fila })}</Selo> : <span className="text-xs text-tinta-3">{t('cartao.semFila')}</span>}
      </div>
    </article>
  );
}
